import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { MessageCircle, Send, X, Settings, BellOff, Bell, ArrowLeftRight, ChevronDown } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

// Popup interno "Chat Team": si apre automaticamente all'arrivo di un messaggio
// team (se auto-apri è on), mantiene un badge con i messaggi da leggere e permette
// di cambiare al volo il destinatario. Non gestisce i canali WhatsApp: per quelli
// si usa il modulo Chat completo.
const SETTINGS_KEY = "aos-team-chat-settings";
const POLL_MS = 8000;
const DEFAULT_EMOJIS = ["👍", "❤️", "😂", "🎉", "✅", "🙏"];

const loadSettings = () => {
  try {
    return { autoOpen: true, muted: false, ...(JSON.parse(localStorage.getItem(SETTINGS_KEY) || "{}")) };
  } catch { return { autoOpen: true, muted: false }; }
};

export function TeamChatWidget() {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const [open, setOpen] = useState(false);
  const [helpMin, setHelpMin] = useState(() => {
    try { return localStorage.getItem("aos-help-minimized") === "1"; } catch { return false; }
  });
  useEffect(() => {
    const onChange = () => {
      try { setHelpMin(localStorage.getItem("aos-help-minimized") === "1"); } catch { /* ignore */ }
    };
    window.addEventListener("aos-help-min-change", onChange);
    return () => window.removeEventListener("aos-help-min-change", onChange);
  }, []);
  const [settings, setSettings] = useState(loadSettings);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [convs, setConvs] = useState([]);
  const [members, setMembers] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [unread, setUnread] = useState(0);
  const [reactFor, setReactFor] = useState(null);
  const scrollRef = useRef(null);
  const lastUnreadRef = useRef(0);

  const persist = (patch) => {
    const next = { ...settings, ...patch };
    setSettings(next);
    try { localStorage.setItem(SETTINGS_KEY, JSON.stringify(next)); } catch { /* noop */ }
  };

  const active = useMemo(() => convs.find((c) => c.id === activeId), [convs, activeId]);

  const loadConversations = useCallback(async () => {
    try {
      const { data } = await api.get("/chat/conversations", { params: { channel: "team" } });
      setConvs(data || []);
      return data || [];
    } catch { return []; }
  }, []);

  const loadMessages = useCallback(async (cid) => {
    if (!cid) return;
    try {
      const { data } = await api.get(`/chat/conversations/${cid}/messages`);
      setMsgs(Array.isArray(data) ? data : (data.items || []));
      setUnread((n) => Math.max(0, n - ((convs.find((c) => c.id === cid) || {}).unread || 0)));
      // il backend segna come letti alla fetch senza `before`
      setConvs((cs) => cs.map((c) => c.id === cid ? { ...c, unread: 0 } : c));
    } catch { /* noop */ }
  }, [convs]);

  const send = async () => {
    const t = input.trim();
    if (!t || !activeId || sending) return;
    setSending(true);
    try {
      await api.post(`/chat/conversations/${activeId}/messages`, { text: t });
      setInput("");
      await loadMessages(activeId);
    } catch { /* noop */ }
    setSending(false);
  };

  const react = async (mid, emoji) => {
    setReactFor(null);
    try {
      await api.post(`/chat/messages/${mid}/reactions`, { emoji });
      await loadMessages(activeId);
    } catch { /* noop */ }
  };

  const startWith = async (memberId) => {
    try {
      const { data } = await api.post("/chat/team/start", { member_ids: [memberId] });
      setPickerOpen(false);
      await loadConversations();
      setActiveId(data.id);
      setOpen(true);
    } catch { /* noop */ }
  };

  // polling unread → apre popup se auto-open e nuovi messaggi
  useEffect(() => {
    if (!user || user.role === "client_user" || user._kind === "portal") return undefined;
    if (location.pathname.startsWith("/portal")) return undefined;
    let timer;
    let alive = true;
    const tick = async () => {
      try {
        const { data } = await api.get("/chat/unread-count", { params: { channel: "team" } });
        if (!alive) return;
        const n = data?.unread || 0;
        const prev = lastUnreadRef.current;
        setUnread(n);
        if (settings.autoOpen && !settings.muted && n > prev && !open) {
          const list = await loadConversations();
          const withUnread = list.find((c) => c.unread > 0);
          if (withUnread) {
            setActiveId(withUnread.id);
            setOpen(true);
          }
        }
        lastUnreadRef.current = n;
      } catch { /* noop */ }
      if (alive) timer = setTimeout(tick, POLL_MS);
    };
    tick();
    return () => { alive = false; clearTimeout(timer); };
  }, [user, location.pathname, settings.autoOpen, settings.muted, open, loadConversations]);

  // quando si apre carica lista + conv attiva
  useEffect(() => {
    if (!open) return;
    (async () => {
      const list = await loadConversations();
      if (!activeId && list.length) setActiveId(list[0].id);
    })();
  }, [open, activeId, loadConversations]);

  useEffect(() => { if (open && activeId) loadMessages(activeId); }, [open, activeId, loadMessages]);
  useEffect(() => {
    if (!open || !activeId) return undefined;
    const iv = setInterval(() => loadMessages(activeId), POLL_MS);
    return () => clearInterval(iv);
  }, [open, activeId, loadMessages]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [msgs, open]);

  const openPicker = async () => {
    try {
      const { data } = await api.get("/chat/team/members");
      setMembers(data || []);
      setPickerOpen(true);
    } catch { /* noop */ }
  };

  if (!user || user.role === "client_user" || user._kind === "portal") return null;
  if (location.pathname.startsWith("/portal")) return null;

  const showBadge = unread > 0 && !settings.muted;
  // Quando la finestra Aiuto è ridotta a icona, la Chat si sposta verso destra
  // per non lasciare troppo spazio vuoto tra le due icone.
  const fabRight = helpMin ? "right-[4.25rem]" : "right-32";
  const panelRight = helpMin ? "right-[4.25rem]" : "right-32";

  return (
    <>
      {open && (
        <div className={`pointer-events-auto fixed bottom-20 ${panelRight} z-[130] flex w-[340px] max-w-[calc(100vw-2rem)] flex-col overflow-hidden rounded-2xl border border-border/70 bg-popover shadow-2xl transition-[right] duration-200`} style={{ height: 460 }} data-testid="team-chat-panel">
          <div className="flex items-center justify-between border-b border-border/60 bg-background px-3 py-2.5">
            <button onClick={openPicker} className="flex min-w-0 flex-1 items-center gap-2 text-left" data-testid="team-chat-active-header">
              <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-foreground text-background"><MessageCircle className="h-4 w-4" /></div>
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-semibold text-foreground">{active?.name || "Chat team"}</div>
                <div className="flex items-center gap-1 text-[10px] text-muted-foreground"><ArrowLeftRight className="h-3 w-3" /> Cambia destinatario</div>
              </div>
              <ChevronDown className="h-4 w-4 text-muted-foreground" />
            </button>
            <div className="ml-2 flex items-center gap-1">
              <button onClick={() => setSettingsOpen((s) => !s)} className="rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground" title="Impostazioni chat" data-testid="team-chat-settings-btn"><Settings className="h-4 w-4" /></button>
              <button onClick={() => { setOpen(false); setSettingsOpen(false); setPickerOpen(false); }} className="rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground" title="Chiudi" data-testid="team-chat-close"><X className="h-4 w-4" /></button>
            </div>
          </div>

          {settingsOpen && (
            <div className="border-b border-border/60 bg-muted/40 px-3 py-2 text-xs" data-testid="team-chat-settings-panel">
              <label className="flex cursor-pointer items-center justify-between py-1.5">
                <span>Apri automaticamente all&apos;arrivo di nuovi messaggi</span>
                <input type="checkbox" checked={settings.autoOpen} onChange={(e) => persist({ autoOpen: e.target.checked })} data-testid="team-chat-toggle-autoopen" />
              </label>
              <label className="flex cursor-pointer items-center justify-between py-1.5">
                <span>Silenzia notifiche (nasconde il contatore)</span>
                <input type="checkbox" checked={settings.muted} onChange={(e) => persist({ muted: e.target.checked })} data-testid="team-chat-toggle-muted" />
              </label>
              <button onClick={() => navigate("/chat")} className="mt-1 text-[11px] font-medium text-foreground underline underline-offset-2 hover:opacity-70" data-testid="team-chat-open-full">Apri la Chat completa →</button>
            </div>
          )}

          {pickerOpen && (
            <div className="border-b border-border/60 bg-background/70 max-h-52 overflow-y-auto" data-testid="team-chat-picker">
              <div className="px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Le tue chat team</div>
              {convs.length === 0 && <div className="px-3 py-2 text-xs text-muted-foreground">Nessuna chat esistente.</div>}
              {convs.map((c) => (
                <button key={c.id} onClick={() => { setActiveId(c.id); setPickerOpen(false); }} className={`flex w-full items-center justify-between px-3 py-1.5 text-left text-sm hover:bg-muted ${c.id === activeId ? "bg-muted/50" : ""}`} data-testid={`team-chat-pick-${c.id}`}>
                  <span className="truncate">{c.name || "Chat team"}</span>
                  {c.unread > 0 && <span className="ml-2 rounded-full bg-foreground px-1.5 py-0.5 text-[10px] font-semibold text-background">{c.unread}</span>}
                </button>
              ))}
              <div className="border-t border-border/60 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Nuova chat con…</div>
              {members.map((m) => (
                <button key={m.id} onClick={() => startWith(m.id)} className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm hover:bg-muted" data-testid={`team-chat-start-${m.id}`}>
                  <span className="flex h-6 w-6 items-center justify-center rounded-full bg-muted text-[10px] font-semibold">{m.initials || (m.name || "?").slice(0, 2).toUpperCase()}</span>
                  <span className="truncate">{m.name}</span>
                </button>
              ))}
            </div>
          )}

          <div ref={scrollRef} className="flex-1 space-y-2 overflow-y-auto px-3 py-2 text-sm" data-testid="team-chat-messages">
            {msgs.length === 0 && <div className="pt-6 text-center text-xs text-muted-foreground">Nessun messaggio.</div>}
            {msgs.map((m, idx) => {
              const mine = m.sender_id === (user?.id || user?._id);
              const myId = user?.id || user?._id;
              const grouped = (m.reactions || []).reduce((acc, r) => {
                const g = acc[r.emoji] || { count: 0, mine: false };
                g.count += 1;
                if (r.user_id === myId) g.mine = true;
                acc[r.emoji] = g;
                return acc;
              }, {});
              const groups = Object.entries(grouped);
              return (
                <div key={m.id} className={`group flex flex-col ${mine ? "items-end" : "items-start"}`} data-testid={`team-chat-msg-${m.id}`}>
                  <div className={`relative flex w-full items-center gap-1 ${mine ? "flex-row-reverse" : "flex-row"}`}>
                    <div className={`max-w-[85%] rounded-2xl px-3 py-1.5 ${mine ? "bg-foreground text-background" : "bg-muted text-foreground"}`}>
                      {!mine && <div className="text-[10px] font-semibold opacity-70">{m.sender_name}</div>}
                      <div className="whitespace-pre-wrap break-words text-[13px]">{m.text || (m.deleted_at ? "Messaggio eliminato" : "")}</div>
                    </div>
                    {!m.deleted_at && (
                      <button
                        onClick={() => setReactFor((id) => (id === m.id ? null : m.id))}
                        className="opacity-0 transition-opacity group-hover:opacity-100 rounded-full p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
                        title="Reagisci"
                        data-testid={`team-chat-react-btn-${m.id}`}
                      >
                        <span className="text-sm">🙂</span>
                      </button>
                    )}
                    {!m.deleted_at && reactFor === m.id && (
                      // Ancorato alla riga full-width (mine=destra, altri=sinistra) così il picker
                      // resta sempre dentro il pannello 340px e tutte le emoji sono cliccabili.
                      <div className={`absolute z-20 flex gap-0.5 rounded-full border border-border/70 bg-popover px-1.5 py-1 shadow-lg ${idx < 2 ? "top-full mt-1" : "bottom-full mb-1"} ${mine ? "right-0" : "left-0"}`} data-testid={`team-chat-react-picker-${m.id}`}>
                        {DEFAULT_EMOJIS.map((e) => (
                          <button key={e} onClick={() => react(m.id, e)} className="rounded-full px-1 text-base leading-none transition-transform hover:scale-125" data-testid={`team-chat-react-${m.id}-${e}`}>{e}</button>
                        ))}
                      </div>
                    )}
                  </div>
                  {groups.length > 0 && (
                    <div className={`mt-0.5 flex flex-wrap gap-1 ${mine ? "justify-end" : "justify-start"}`} data-testid={`team-chat-reactions-${m.id}`}>
                      {groups.map(([emoji, g]) => (
                        <button
                          key={emoji}
                          onClick={() => react(m.id, emoji)}
                          className={`flex items-center gap-0.5 rounded-full border px-1.5 py-0.5 text-[11px] transition-colors ${g.mine ? "border-sky-500/50 bg-sky-500/15 text-foreground" : "border-border/60 bg-muted/60 text-muted-foreground hover:bg-muted"}`}
                          data-testid={`team-chat-reaction-${m.id}-${emoji}`}
                        >
                          <span className="text-xs leading-none">{emoji}</span>
                          <span className="font-medium">{g.count}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div className="border-t border-border/60 bg-background/70 p-2">
            <div className="flex items-end gap-1.5">
                <textarea value={input} onChange={(e) => setInput(e.target.value)} rows={1}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                placeholder={activeId ? "Scrivi al team…" : "Seleziona un destinatario"}
                disabled={!activeId}
                className="max-h-20 flex-1 resize-none rounded-lg border border-border/60 bg-background px-3 py-2 text-sm outline-none focus:border-foreground/30"
                data-testid="team-chat-input" />
              <button onClick={send} disabled={sending || !activeId || !input.trim()} className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-foreground text-background disabled:opacity-40" data-testid="team-chat-send">
                <Send className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      )}

      <button
        onClick={() => setOpen((o) => !o)}
        className={`pointer-events-auto fixed bottom-6 ${fabRight} z-[130] flex h-10 w-10 items-center justify-center rounded-full bg-foreground text-background shadow-xl transition-[right,transform] duration-200 hover:scale-105`}
        data-testid="team-chat-fab"
        title="Chat team"
      >
        {settings.muted ? <BellOff className="h-4 w-4" /> : <MessageCircle className="h-4 w-4" />}
        {showBadge && (
          <span className="pointer-events-none absolute -right-1 -top-1 flex min-w-[16px] items-center justify-center rounded-full bg-sky-500 px-1 text-[10px] font-semibold text-white" data-testid="team-chat-unread-badge">
            {unread > 99 ? "99+" : unread}
          </span>
        )}
        {settings.muted && unread > 0 && (
          <span className="pointer-events-none absolute -right-1 -top-1 h-2 w-2 rounded-full bg-sky-500" />
        )}
      </button>
    </>
  );
}

export default TeamChatWidget;
